/**
 * CONFIGURAZIONE DELL'AZIONE "aggiorna campo" (`crm.update-field`).
 *
 * Due scelte in cascata — QUALE campo dell'entità toccare e con QUALE valore — dove la seconda
 * dipende dalla prima: il tipo del campo decide come si chiede il valore, e per gli enumerati anche
 * quali valori esistono. Il vocabolario arriva tutto dal catalogo dei campi SCRIVIBILI
 * (`updatable-fields`), che è elenco separato dai filtrabili: la data di creazione si filtra e non
 * si scrive, l'importo di un preventivo nasce dalle sue righe.
 *
 * ## Cosa finisce nella config
 *
 * `{ fieldName, value }` — piatta, le stesse due chiavi che
 * `UpdateFieldAutomationActionType.validate` rilegge. L'entità contro cui campo e valore vengono
 * giudicati **non** viaggia nella config: la riceve in firma chi valida, dal blocco QUANDO.
 * Questo form la usa comunque — come prop, non come chiave — per chiedere il catalogo dei campi
 * scrivibili, che dall'entità dipende.
 *
 * ## Stessi controlli del blocco SE
 *
 * Campo e valore si rendono con `ChipSelect` e `CatalogValueField`, gli stessi della riga
 * condizione: una configurazione "aggiorna campo" è una riga condizione senza l'operatore, e deve
 * leggersi come tale invece di somigliarle. La `cardinality` è fissa a `SINGLE` — la portava
 * l'operatore, e qui operatori non ce ne sono.
 *
 * Ne segue che la sola lettura qui non si gestisce affatto: sono `<button>` e `<input>` veri,
 * quindi li spegne il `<fieldset disabled>` della modale. È la ragione per cui il contratto dei form
 * di configurazione non porta nessun flag — attenersi agli editor condivisi lo rende superfluo.
 *
 * ## Il valore è già TIPIZZATO nella config
 *
 * Numeri come numeri, booleani come booleani, date e stringhe come stringhe: è la forma che
 * `ParamType.accepts` pretende lato server. La tipizzazione avviene **qui** e non al confine col
 * DTO, dove il QUANDO e il SE invece la fanno, perché quel confine è generico e la config è opaca —
 * il `type` del campo scelto lo conosce solo questo form.
 *
 * Resta però la ragione per cui gli altri tengono stringhe: coercizzare a ogni battuta rende
 * impossibile digitare un decimale, `1.` diventa `1` e il punto sparisce sotto le dita. Per questo
 * il valore grezzo vive in uno stato LOCALE e la config riceve solo ciò che è già un valore, con la
 * chiave omessa finché non lo è. Sugli ENUM la distinzione non si vede: il catalogo espone i valori
 * come stringhe anche quando l'enum di dominio è numerico, e grezzo e tipizzato coincidono.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { typedValue } from '../automationMappers';
import { fetchAutomationUpdatableFields } from '../automationThunks';
import { CatalogValueField, ChipSelect } from '../catalogFieldEditors';
import type { ActionConfigProps } from './automationActionConfig';

export default function UpdateFieldActionConfig({
  className,
  config,
  onChange,
}: ActionConfigProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const updatableFieldsByClassName = useAppSelector(
    (s) => s.automations.updatableFieldsByClassName,
  );
  // Chiave ASSENTE (catalogo ancora in volo) e lista VUOTA (entità senza campi scrivibili) sono
  // stati diversi, e appiattirli fa dire il falso: riaprendo un'azione già configurata, il campo
  // c'è ma non è ancora risolvibile, e la UI direbbe "scegli prima il campo". Stessa distinzione
  // che il riepilogo fa fra catalogo in arrivo e contenuto assente.
  const catalog = updatableFieldsByClassName[className];
  const isLoadingCatalog = catalog === undefined;
  const fields = catalog ?? [];

  // Catalogo lazy per className, con la stessa cache degli altri due: due azioni "aggiorna campo"
  // sulla stessa entità non fanno due richieste.
  useEffect(() => {
    if (className && !updatableFieldsByClassName[className])
      dispatch(fetchAutomationUpdatableFields(className));
  }, [className, updatableFieldsByClassName, dispatch]);

  // `as string | undefined` e non `as string`: la config è opaca (`Record<string, unknown>`), quindi
  // il cast è un'asserzione nostra — dichiararla nullable dice il vero e lascia lavorare il `??`.
  const fieldName = (config?.fieldName as string | undefined) ?? '';
  const field = fields.find((f) => f.fieldName === fieldName);

  // Il valore mostrato a video è GREZZO e vive qui, in locale, mentre nella config finisce già
  // tipizzato. Le due cose non coincidono durante la digitazione: `typedValue` su un numero
  // incompleto ("1.") torna null, e mostrare il tipizzato farebbe sparire il punto sotto le dita —
  // è la stessa ragione per cui il QUANDO e il SE tengono stringhe nell'editor. Lì il boundary che
  // tipizza è il mapper; qui è il form, perché è l'unico a conoscere il `type` del campo scelto.
  // Inizializzato dalla config: la modale monta il form solo quando si apre, quindi basta il valore
  // iniziale — non serve risincronizzare a ogni render.
  const [rawInput, setRawInput] = useState(() => {
    const initial = config?.value;

    return initial === null || initial === undefined ? '' : String(initial);
  });

  /**
   * La config si riscrive INTERA a ogni modifica, non a delta: è il form l'unico a conoscerne la
   * forma, e restituirne una parziale obbligherebbe chi lo ospita a saperla ricomporre.
   *
   * La chiave `value` viene OMESSA quando non c'è un valore, invece di essere valorizzata a vuoto:
   * è così che il server distingue "non configurato" da "valore sbagliato", e vale anche per il
   * numero incompleto — non si spedisce NaN, si tace, esattamente come fa `eventConfigToDto`.
   */
  const emit = (nextFieldName: string, raw: string) => {
    const nextField = fields.find((f) => f.fieldName === nextFieldName);
    const value = nextField && raw !== '' ? typedValue(nextField.type, raw) : null;
    const next: Record<string, unknown> = { fieldName: nextFieldName };

    if (value !== null) next.value = value;

    onChange(next);
  };

  // Ordinamento alfabetico per etichetta localizzata, come per oggetti ed eventi del QUANDO:
  // dipende dalla locale, che conosce il client. Copia prima di ordinare — `fields` viene dallo
  // store e non si muta.
  const fieldOptions = [...fields].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="builder-crow">
      {/* Stessa resa della riga condizione: chip per il campo, editor del valore scelto dal tipo.
          Non è coerenza estetica soltanto — questi controlli sono `<button>` e `<input>` veri,
          quindi il `<fieldset disabled>` di chi ci ospita li spegne da sé, senza `disabled`
          espliciti da ricordare a ogni chiamata (una Select di MUI non lo permetteva). */}
      <ChipSelect
        value={fieldName}
        options={fieldOptions.map((f) => ({ value: f.fieldName, label: f.label }))}
        placeholder={t('automation.form.fieldToUpdate')}
        // Il placeholder sparisce alla prima scelta, e da lì il chip si annuncerebbe col solo nome
        // del campo — senza dire che è il campo DA AGGIORNARE.
        ariaLabel={t('automation.form.fieldToUpdate')}
        // Cambiare campo azzera il valore: apparteneva al campo precedente, e su un enumerato
        // sarebbe un codice di un altro vocabolario — accettato dal tipo, rifiutato dal dominio.
        onChange={(next) => {
          setRawInput('');
          emit(next, '');
        }}
      />

      {/* Il valore compare solo a campo scelto: prima non si saprebbe né come chiederlo né fra
          quali valori. Finché manca, una riga di aiuto invece di un input inerte — ma solo se il
          campo è davvero da scegliere: col catalogo ancora in volo si tace, perché un'azione già
          configurata sarebbe accusata di non avere un campo che invece ha. */}
      {!field && !isLoadingCatalog && (
        <span className="text-sm text-muted">{t('automation.form.selectFieldFirst')}</span>
      )}

      {/* `SINGLE` fisso: la cardinalità la porta l'operatore, e qui operatori non ce ne sono — si
          assegna un valore, non si confronta. RANGE e SET restano quindi fuori dal giro. */}
      {field && (
        <CatalogValueField
          field={field}
          cardinality="SINGLE"
          value={rawInput}
          onChange={(next) => {
            setRawInput(next);
            emit(fieldName, next);
          }}
        />
      )}
    </div>
  );
}
